import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mic } from 'lucide-react'
import { fetchProgress } from '@/lib/learn/client'
import { isLearnPreviewUnlocked } from '@/lib/learn/preview'
import type { ProgressResponse } from '@/lib/learn/types'
import './learn-theme.css'

const DISMISS_KEY = 'baba.learn.warmup_card_dismissed_on'

function todayKey(): string {
  const d = new Date()
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`
}

/**
 * The one cross-sell from translating into speaking, on the surface people
 * actually open. Three rules keep it from becoming an ad:
 *
 *  - It only appears when there is something REAL to do — words due today, or
 *    a streak that hasn't been extended yet. A card that shows every day is
 *    furniture, and furniture gets ignored.
 *  - It states the number. "6 words are ready" is a reason; "Practice speaking"
 *    is a slogan.
 *  - Dismissing it silences it for the rest of the day, not forever: the
 *    reason it appears changes daily, so a permanent dismissal would throw
 *    away a genuinely different prompt tomorrow.
 */
export default function LearnWarmUpCard() {
  const navigate = useNavigate()
  const [progress, setProgress] = useState<ProgressResponse | null>(null)
  const [dismissed, setDismissed] = useState(true)

  useEffect(() => {
    // Learn is behind the preview gate; an invitation to a locked door is worse
    // than no invitation.
    if (!isLearnPreviewUnlocked()) return
    let alive = true
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === todayKey())
    } catch {
      setDismissed(false)
    }
    fetchProgress()
      .then((p) => {
        if (alive) setProgress(p)
      })
      .catch(() => {
        // A signed-out or errored read simply means no card.
      })
    return () => {
      alive = false
    }
  }, [])

  if (dismissed || !progress) return null
  const due = progress.dueToday
  const streakAtRisk = progress.streak.current > 0 && !progress.streak.extendedToday
  if (due === 0 && !streakAtRisk) return null

  const headline =
    due > 0
      ? `${due} ${due === 1 ? 'word is' : 'words are'} ready to say out loud`
      : `Keep your ${progress.streak.current}-day streak`
  const sub = due > 0 ? 'One round. Two minutes of talking.' : 'One round is all it takes today.'

  const dismiss = () => {
    setDismissed(true)
    try {
      localStorage.setItem(DISMISS_KEY, todayKey())
    } catch {
      // ignore
    }
  }

  return (
    <div className="learn-scope mx-auto w-full max-w-2xl px-4 pb-4">
      <div
        className="flex items-center gap-3 rounded-3xl px-4 py-3"
        style={{ background: 'var(--learn-surface)', border: '1px solid var(--learn-border)', boxShadow: 'var(--learn-shadow-clay)' }}
      >
        <span
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full"
          style={{ background: 'var(--learn-terracotta)', color: 'var(--learn-on-cta)' }}
          aria-hidden="true"
        >
          <Mic size={18} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold" style={{ color: 'var(--learn-ink)' }}>
            {headline}
          </span>
          <span className="block truncate text-xs" style={{ color: 'var(--learn-muted)' }}>
            {sub}
          </span>
        </span>
        <button
          type="button"
          onClick={() => navigate('/learn/play')}
          className="shrink-0 rounded-full px-4 py-2 text-sm font-semibold"
          style={{ background: 'var(--learn-terracotta)', color: 'var(--learn-on-cta)' }}
        >
          Warm up
        </button>
        <button type="button" onClick={dismiss} aria-label="Not today" className="shrink-0 px-1 text-lg leading-none" style={{ color: 'var(--learn-muted)' }}>
          ×
        </button>
      </div>
    </div>
  )
}
